import type { MediaInfo } from './inspect';
import type { WebCodecsDecodeStrategy, WebCodecsSupport } from './webcodecs';
import { targetBudget } from './budget';

// Wave's outputs: MP4 (primary) and MP3 (one bonus path — audio extraction
// from a video source). See `project-scope-video-to-mp4-only` in memory.
export type OutputFormat = 'mp4' | 'mp3';
export type ConvertMethod = 'remux' | 'transcode';
/** Which backend the plan wants convert.ts to drive. `ffmpeg` is the classic
 *  wasm ffmpeg path; `webcodecs` is the browser-native H.264 fast lane. */
export type ConvertEncoder = 'ffmpeg' | 'webcodecs';

export interface ConvertPlan {
  format: OutputFormat;
  method: ConvertMethod;
  encoder: ConvertEncoder;
  /** How the video track is decoded when `encoder === 'webcodecs'`. `browser`
   *  is the fast lane (HTMLVideoElement). `ffmpeg` is Path B — ffmpeg-wasm
   *  decodes raw frames and feeds them to the WebCodecs H.264 encoder, so
   *  sources the browser can't decode still get an H.264 output. Undefined
   *  on non-webcodecs plans. */
  decodeStrategy?: WebCodecsDecodeStrategy;
  /** Video codec the plan wants at the encoder + muxer. `h264` is the
   *  default; `hevc` fires only when the source is HDR / 10-bit AND the
   *  browser has an HEVC Main-10 encoder AND we're on the browser-decode
   *  fast lane (Path B doesn't currently preserve 10-bit). Undefined on
   *  non-webcodecs plans. */
  videoCodec?: 'h264' | 'hevc';
  reason: string;
  /** ffmpeg-cli arguments. Meaningful only when `encoder === 'ffmpeg'`;
   *  empty for `webcodecs` (which drives its own pipeline). */
  args: string[];
  outputName: string;
  /** Rough estimate of the output size in bytes (null when unknowable). */
  estimatedOutputBytes: number | null;
  /** Rough estimate of how long the conversion will take, in milliseconds. */
  estimatedDurationMs: number | null;
  /** Encoder settings for `webcodecs` plans. */
  encode?: VideoEncodeSettings;
  /** Audio handling for `webcodecs` plans. */
  audio?: AudioEncodeSettings;
  /** Section of the source to keep, in seconds. */
  trim?: { start: number; end: number } | null;
  /** Set when the requested options can't produce a sensible file (e.g. a
   *  target size far too small for the length). The UI shows it and keeps
   *  the convert button disabled. */
  blocker?: string;
  /** Requested output size in bytes, when compressing to a target. */
  targetBytes?: number | null;
}

/** User-facing conversion options. Every field is optional; an empty object
 *  plans exactly what Wave did before options existed. */
export interface PlanOptions {
  /** Re-encode HEVC / AV1 / 10-bit video to 8-bit H.264 so the MP4 plays on
   *  every device, tone-mapping HDR to standard colours. */
  compatibility?: boolean;
  /** Aim the MP4 at this total size in bytes. Forces an H.264 re-encode at a
   *  computed bitrate, stepping resolution down when the budget is tight. */
  targetBytes?: number | null;
  /** Cap on the output's short edge in pixels (1080, 720, 480...). Forces a
   *  re-encode when the source is larger. Never upscales. */
  maxShortEdge?: number | null;
  /** Leave the audio track out of the MP4. */
  removeAudio?: boolean;
  /** Keep only this section. `exact` re-encodes for a frame-accurate start;
   *  otherwise a stream copy starts at the keyframe at or before `start`. */
  trim?: { start: number; end: number; exact?: boolean } | null;
  /** Extra clockwise rotation on top of the source's own. */
  rotate?: 0 | 90 | 180 | 270;
}

export interface VideoEncodeSettings {
  /** Output dimensions, in display orientation (rotation already applied). */
  width: number;
  height: number;
  bitrate: number;
  bitrateMode: 'variable' | 'constant';
  /** Source HDR transfer to tone-map to SDR, or null for SDR sources. */
  toneMap: 'pq' | 'hlg' | null;
  /** Total clockwise rotation baked into the pixels (source tag + extra). */
  rotation: 0 | 90 | 180 | 270;
}

export interface AudioEncodeSettings {
  remove: boolean;
  /** AAC bitrate override in kbps; null keeps the per-channel default. */
  bitrateKbps: number | null;
  /** Channel count override (e.g. 2 to fold 5.1 down for small targets). */
  channels: number | null;
}

// The two planner helpers return the plan shape without the estimate fields;
// `planConversion` computes those centrally and attaches them.
type CorePlan = Omit<ConvertPlan, 'estimatedOutputBytes' | 'estimatedDurationMs'>;

// Codecs that can sit inside an MP4 without re-encoding. Aliases (h265/hevc,
// x264/h264, mp4a/aac) are normalised by `normalizeCodec` first.
//
// Opus is deliberately not on the audio list even though MP4 formally supports
// it — Apple / older Windows players don't decode Opus-in-MP4 reliably, so
// Wave re-encodes to AAC for the safer "plays everywhere" promise.
//
// MPEG-4 Part 2 (the codec inside most Xvid/DivX AVIs) is legal inside MP4
// but Chrome, Safari, iOS, Android's built-in player and every consumer web
// tool have no decoder for it — the output file plays back as a black,
// zero-dimension track. Keeping it out of the remux-eligible set forces
// AVI / DIVX sources through the WebCodecs H.264 transcode path instead,
// so the MP4 users make in the same browser can actually be previewed in it.
const MP4_VIDEO_CODECS = new Set(['h264', 'hevc', 'av1']);
// AC-3 / E-AC-3 are legal in MP4 but Chrome and Firefox have no decoder for
// them, so the MP4 plays silent there. They are converted to AAC instead.
const MP4_AUDIO_CODECS = new Set(['aac', 'mp3']);

// LGPL-only encoders available in @ffmpeg/core.
// H.264 via libx264 is deliberately excluded (GPL). WebCodecs will slot in as
// a preferred encoder path in a follow-up — see `project-transcode-backend`.
const FALLBACK_VIDEO_CODEC = 'mpeg4';
const FALLBACK_AUDIO_CODEC = 'aac';

/** Returns `-f <fmt>` args when the source was resolved to a raw elementary
 *  stream by `inspectFile` (extension hint + byte-level sniff), empty
 *  otherwise. Never guesses from the filename — a `.mkv` file that a user
 *  renamed to `.h264` MUST NOT get `-f h264` slapped on it or ffmpeg parses
 *  the container as raw bitstream garbage and aborts. */
function rawFmtArgs(info: MediaInfo): string[] {
  return info.rawStreamFormat ? ['-f', info.rawStreamFormat] : [];
}

// Robustness switches applied to every ffmpeg invocation. `+genpts` regenerates
// presentation timestamps for containers with broken/missing timing (some AVI,
// MPEG-TS captures); `max_muxing_queue_size` avoids "Too many packets buffered
// for output stream" on inputs with widely-interleaved streams.
const INPUT_ROBUSTNESS = ['-fflags', '+genpts+igndts'];
const MUX_ROBUSTNESS = ['-max_muxing_queue_size', '1024'];

// mpeg4 encoder quality knobs. Quality-based encoding (qscale) delivers
// noticeably better fidelity per bit than a fixed bitrate for unknown sources,
// so Wave picks a qscale from the source resolution and lets the encoder size
// the bits. mbd=2 is rate-distortion mode decision; +mv4 enables 4-motion-
// vector per macroblock; +aic turns on AC prediction; trellis 2 is trellis
// quantisation — all standard mpeg4 quality improvements over the defaults.
const MPEG4_TUNING = [
  '-mbd', '2',
  '-flags', '+mv4+aic',
  '-trellis', '2',
  '-cmp', '2',
  '-subcmp', '2',
  '-g', '250',
];

function normalizeCodec(raw: string): string {
  const c = raw.toLowerCase();
  if (c === 'h265' || c === 'x265') return 'hevc';
  if (c === 'x264' || c === 'avc' || c === 'avc1') return 'h264';
  if (c === 'mp4a') return 'aac';
  // mp3float / mp3adu are decoder aliases for real MPEG-1 Layer III; mp2 is
  // MPEG-1 Layer II, a different codec that MP4 doesn't hold and that can't
  // be repackaged into an .mp3 either. Keep it distinct so the planner takes
  // the re-encode path in both cases.
  if (c === 'mp3float' || c === 'mp3adu') return 'mp3';
  return c;
}

/** Pick an mpeg4 qscale (1 best, 31 worst) that suits the source's resolution.
 *  Higher-resolution sources deserve more bits per pixel — a good qscale is
 *  more consistent across sources than any fixed bitrate. */
function pickQscale(w: number | null, h: number | null): number {
  const shortEdge = w && h ? Math.min(w, h) : 720;
  if (shortEdge >= 2160) return 3;
  if (shortEdge >= 1440) return 3;
  if (shortEdge >= 1080) return 4;
  if (shortEdge >= 720) return 4;
  if (shortEdge >= 480) return 5;
  return 6;
}

function qualityLabel(qscale: number): string {
  if (qscale <= 3) return 'near-source quality';
  if (qscale <= 4) return 'high quality';
  if (qscale <= 5) return 'good quality';
  return 'balanced quality';
}

/** Rough output size + duration estimate. Deliberately approximate — the
 *  numbers are for setting user expectations, not for accounting. Real
 *  in-browser transcoding varies with the user's CPU and browser. */
function estimateOutput(
  info: MediaInfo,
  format: OutputFormat,
  method: ConvertMethod,
  encoder: ConvertEncoder,
  decodeStrategy: WebCodecsDecodeStrategy | undefined,
  videoCopied = false,
  encode?: VideoEncodeSettings,
  audio?: AudioEncodeSettings,
): { bytes: number | null; ms: number | null } {
  const dur = info.durationSeconds;
  if (!dur || dur <= 0) return { bytes: null, ms: null };

  if (format === 'mp3') {
    if (method === 'remux' && info.audio?.bitrate) {
      return {
        bytes: Math.round((info.audio.bitrate * dur) / 8),
        ms: Math.max(2000, dur * 20),
      };
    }
    return {
      bytes: Math.round((190_000 * dur) / 8),
      ms: Math.max(3000, dur * 80),
    };
  }

  if (method === 'remux') {
    // A dropped audio track takes its share of the file with it.
    const audioBytes = audio?.remove && info.audio?.bitrate ? (info.audio.bitrate * dur) / 8 : 0;
    return { bytes: Math.round(Math.max(0, info.sizeBytes - audioBytes)), ms: Math.max(2000, dur * 12) };
  }

  // Video copied, audio converted to AAC: size is the source's video bytes
  // plus the new audio; time is an audio encode plus a remux pass.
  if (videoCopied) {
    const srcAudioBytes = info.audio?.bitrate ? (info.audio.bitrate * dur) / 8 : 0;
    const ch = Math.min(6, Math.max(1, info.audio?.numChannels ?? 2));
    const aacBytes = (Math.max(128, Math.min(384, ch * 96)) * 1000 * dur) / 8;
    const bytes = info.sizeBytes ? Math.round(Math.max(0, info.sizeBytes - srcAudioBytes) + aacBytes) : null;
    // QA: a 30 s 1080p H.264 + PCM .mov took 8 s on a mid-range laptop.
    return { bytes, ms: Math.max(2500, dur * 250) };
  }

  const w = info.video?.width ?? 1280;
  const h = info.video?.height ?? 720;
  const fps = info.video?.fps ?? 30;
  const shortEdge = Math.min(w, h);
  const audioBitrate = !info.audio || audio?.remove ? 0 : (audio?.bitrateKbps ?? 192) * 1000;

  if (encoder === 'webcodecs') {
    // H.264 is roughly 2× more efficient than mpeg4 at the same quality,
    // so the bpp curve is materially lower. Numbers picked from
    // eyeballing typical Chrome hardware-encoder output at Wave's
    // bitrate curve. A constant-bitrate encode (target size) lands close
    // to its requested rate, so that one is used as-is.
    const bpp =
      shortEdge >= 1440 ? 0.055 :
      shortEdge >= 1080 ? 0.05 :
      shortEdge >= 720  ? 0.045 : 0.05;
    const videoBitrate = encode
      ? (encode.bitrateMode === 'constant'
          ? encode.bitrate
          : Math.min(encode.bitrate, encode.width * encode.height * fps * bpp))
      : w * h * fps * bpp;
    const bytes = Math.round(((videoBitrate + audioBitrate) * dur) / 8);
    let speedFactor: number;
    if (decodeStrategy === 'ffmpeg') {
      // Path B: wasm-decode is the bottleneck (single-threaded, no SIMD in
      // many builds). Still ~2× the plain mpeg4 fallback because the encode
      // half is hardware. These numbers are eyeballed off a WMV → H.264
      // benchmark on a mid-range Intel laptop.
      speedFactor =
        shortEdge >= 2160 ? 0.15 :
        shortEdge >= 1440 ? 0.25 :
        shortEdge >= 1080 ? 0.4 :
        shortEdge >= 720  ? 0.9 : 1.6;
    } else {
      // 'browser' strategy: hardware decode + hardware encode. Typical
      // 2–10× realtime for common sources; slowest-per-resolution so the
      // estimate rarely undershoots.
      speedFactor =
        shortEdge >= 2160 ? 1.2 :
        shortEdge >= 1440 ? 2.0 :
        shortEdge >= 1080 ? 3.0 :
        shortEdge >= 720  ? 4.0 : 6.0;
    }
    // HDR → SDR tone mapping runs on the GPU, but the decoder hands over
    // 10-bit frames, twice the bytes of the usual 8-bit ones.
    if (encode?.toneMap) speedFactor /= 1.2;
    const ms = Math.max(3000, Math.round((dur / speedFactor) * 1000));
    return { bytes, ms };
  }

  // Bits per pixel per frame for the mpeg4 qscale curve Wave targets.
  const bpp = shortEdge >= 1440 ? 0.12 : shortEdge >= 1080 ? 0.10 : shortEdge >= 720 ? 0.09 : 0.08;
  const videoBitrate = w * h * fps * bpp;
  const bytes = Math.round(((videoBitrate + audioBitrate) * dur) / 8);
  // Rough single-thread wasm mpeg4 encode speed. Halves per resolution tier.
  const speedFactor =
    shortEdge >= 2160 ? 0.05 :
    shortEdge >= 1440 ? 0.10 :
    shortEdge >= 1080 ? 0.18 :
    shortEdge >= 720 ? 0.35 :
    shortEdge >= 480 ? 0.7 : 1.2;
  const ms = Math.max(4000, Math.round((dur / speedFactor) * 1000));
  return { bytes, ms };
}

function describeSource(info: MediaInfo): string {
  const parts: string[] = [];
  if (info.video) {
    const res = info.video.width && info.video.height ? `${info.video.width}×${info.video.height}` : '';
    parts.push(res ? `${info.video.codec} ${res}` : info.video.codec);
  }
  if (info.audio) parts.push(info.audio.codec);
  return parts.join(' + ') || 'unknown streams';
}

export function planConversion(
  info: MediaInfo,
  inputName: string,
  format: OutputFormat = 'mp4',
  webcodecs: WebCodecsSupport | null = null,
  options: PlanOptions = {},
): ConvertPlan {
  const resolved = resolveOptions(info, options);
  const plan: CorePlan =
    format === 'mp3' ? planMp3(info, inputName, resolved) : planMp4(info, inputName, webcodecs, resolved);
  const videoCopied = plan.args.some((a, i) => a === '-c:v' && plan.args[i + 1] === 'copy');
  // Estimates run against the section being kept, so a 10-second trim of a
  // one-hour file doesn't promise an hour's worth of output.
  const timed: MediaInfo = resolved.trim && info.durationSeconds
    ? {
        ...info,
        durationSeconds: resolved.trim.end - resolved.trim.start,
        sizeBytes: Math.round(info.sizeBytes * ((resolved.trim.end - resolved.trim.start) / info.durationSeconds)),
      }
    : info;
  const est = estimateOutput(timed, plan.format, plan.method, plan.encoder, plan.decodeStrategy, videoCopied, plan.encode,
    plan.audio ?? { remove: resolved.removeAudio, bitrateKbps: null, channels: null });
  let bytes = est.bytes;
  if (plan.targetBytes && bytes != null) bytes = Math.min(bytes, plan.targetBytes);
  return { ...plan, estimatedOutputBytes: bytes, estimatedDurationMs: est.ms };
}

interface ResolvedOptions {
  compatibility: boolean;
  targetBytes: number | null;
  maxShortEdge: number | null;
  removeAudio: boolean;
  trim: { start: number; end: number; exact: boolean } | null;
  rotate: 0 | 90 | 180 | 270;
}

/** Clamp and sanity-check user options against the source. Anything that
 *  wouldn't change the output (a trim covering the whole file, a resolution
 *  cap above the source) is dropped here so the planner never re-encodes
 *  for nothing. */
function resolveOptions(info: MediaInfo, o: PlanOptions): ResolvedOptions {
  const dur = info.durationSeconds ?? null;
  let trim: ResolvedOptions['trim'] = null;
  if (o.trim && Number.isFinite(o.trim.start) && Number.isFinite(o.trim.end)) {
    const start = Math.max(0, o.trim.start);
    const end = dur ? Math.min(dur, o.trim.end) : o.trim.end;
    const coversAll = start <= 0.05 && (!dur || end >= dur - 0.05);
    if (end - start >= 0.1 && !coversAll) trim = { start, end, exact: !!o.trim.exact };
  }
  const srcShort = info.video
    ? Math.min(info.video.displayWidth ?? info.video.width ?? 0, info.video.height ?? 0)
    : 0;
  const maxShortEdge = o.maxShortEdge && srcShort && o.maxShortEdge < srcShort ? o.maxShortEdge : null;
  const targetBytes = o.targetBytes && o.targetBytes > 0 ? Math.round(o.targetBytes) : null;
  return {
    compatibility: !!o.compatibility,
    targetBytes,
    maxShortEdge,
    removeAudio: !!o.removeAudio,
    trim,
    rotate: normaliseRotation(o.rotate ?? 0),
  };
}

function normaliseRotation(deg: number): 0 | 90 | 180 | 270 {
  const n = ((Math.round(deg) % 360) + 360) % 360;
  return n === 90 || n === 180 || n === 270 ? n : 0;
}

const even = (n: number): number => Math.max(2, Math.round(n / 2) * 2);

/** Default H.264 bitrate for an output size - the same bits-per-pixel curve
 *  the WebCodecs path has always used, applied to the OUTPUT dimensions so a
 *  downscaled encode gets a proportionally smaller bitrate. */
function defaultVideoBitrate(w: number, h: number, fps: number): number {
  const shortEdge = Math.min(w, h);
  const bpp =
    shortEdge >= 2160 ? 0.10 :
    shortEdge >= 1440 ? 0.09 :
    shortEdge >= 1080 ? 0.08 :
    shortEdge >= 720  ? 0.07 :
    shortEdge >= 480  ? 0.08 : 0.10;
  return Math.min(50_000_000, Math.max(400_000, Math.round(w * h * fps * bpp)));
}

function formatMb(bytes: number): string {
  // Decimal megabytes, the unit Discord, WhatsApp and mail providers quote.
  const mb = bytes / 1_000_000;
  return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

/** 65 → "1:05", 65.5 → "1:05.5" - tenths only when the time has them. */
function formatClock(seconds: number): string {
  const tenths = Math.round(Math.max(0, seconds) * 10);
  const s = Math.floor(tenths / 10);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0') + (tenths % 10 ? `.${tenths % 10}` : '');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** Args that map text subtitle streams into the MP4 output. When any sub
 *  is bitmap-based (PGS / VobSub / DVB), we skip subs entirely — mixing
 *  passes fails, and dropping is safer than crashing the whole convert. */
function subtitleMapArgs(subs: MediaInfo['subtitles']): string[] {
  if (!subs || !subs.length) return [];
  const anyBitmap = subs.some((s) => !s.text);
  if (anyBitmap) return [];
  return ['-map', '0:s?'];
}

/** Args that tell the encoder how to write the mapped subtitle streams.
 *  Only fired when we actually mapped subs — otherwise the mp4 muxer would
 *  complain about a codec option with no matching stream. */
function subtitleCodecArgs(subs: MediaInfo['subtitles']): string[] {
  if (!subs || !subs.length) return [];
  if (subs.some((s) => !s.text)) return [];
  // `mov_text` is the only text-subtitle codec the ISO base file format
  // (MP4 / MOV) actually supports. Everything text-based converts to it
  // — even mov_text itself, so a copy pass isn't worth the branch.
  return ['-c:s', 'mov_text'];
}

function hasWebCodecsH264(s: WebCodecsSupport | null): boolean {
  if (!s || !s.hasVideoEncoder) return false;
  return s.h264Baseline || s.h264Main || s.h264High;
}

/** Which decode strategy (if any) fits this source under WebCodecs. The
 *  `browser` strategy is preferred: it uses the browser's own hardware
 *  video decoder and finishes several × realtime. The `ffmpeg` strategy is
 *  Path B — ffmpeg-wasm decodes raw frames for sources the browser can't
 *  handle, and we still get an H.264 output instead of falling to mpeg4.
 *  Returns null when neither applies (no encoder, no dimensions, etc.). */
/** HEVC copied out of MKV / TS keeps its `hev1` sample entry, which QuickTime,
 *  Safari and iOS refuse to play. `hvc1` is the same bitstream with parameter
 *  sets in the sample description — the tag Apple requires. */
function hevcTagArgs(videoCodec: string | null): string[] {
  return videoCodec === 'hevc' ? ['-tag:v', 'hvc1'] : [];
}

// Video codecs @ffmpeg/core can't decode, so the browser's own decoder (Path A)
// is the only way in. Filled from QA against the shipped wasm build.
const BROWSER_ONLY_DECODE = new Set<string>([]);

function pickWebCodecsStrategy(
  info: MediaInfo,
  support: WebCodecsSupport | null,
  needsFilters = false,
): WebCodecsDecodeStrategy | null {
  if (!hasWebCodecsH264(support)) return null;
  if (!info.video || !info.video.width || !info.video.height) return null;
  // Path C decodes with the browser's VideoDecoder, usually on the graphics
  // chip - an order of magnitude faster than Path B's wasm decode - and does
  // its own trimming, rotation and scaling. inspectFile has already opened
  // the track and asked the decoder (`hardwareDecodable`); if the decoder
  // still fails partway, the transcode drops to Path B by itself.
  if (info.hardwareDecodable) return 'webcodecs';
  // Trims, extra rotation and tone mapping otherwise run in Path B's ffmpeg
  // filter chain; Path A's <video> capture has no equivalent.
  if (needsFilters) return 'ffmpeg';
  // Path A ('browser') captures frames off a playing <video> element, and any
  // frame the capture misses is silently dropped — QA on 2026-10-06 saw 3-48%
  // of frames lost and duplicate timestamps on VP8 / VP9 / AV1 / H.264
  // sources, worse in background tabs. Path B decodes every frame
  // deterministically, so it is the default. Path A is kept only for codecs
  // the wasm build has no decoder for.
  const src = normalizeCodec(info.video.codec);
  if (BROWSER_ONLY_DECODE.has(src) && support!.browserDecodableVideoCodecs.includes(src)) return 'browser';
  // Path B walks the source in `-ss` / `-t` segments. When the container
  // advertises a duration we use it for scheduling + progress; when it
  // doesn't (live-capture MKVs, some screen-record containers), the
  // decode loop still runs — it just stops when a segment yields no
  // frames instead of counting down to a known end. Progress ETA falls
  // back to indeterminate but the output is still H.264.
  return 'ffmpeg';
}

/** ffmpeg filter chain that tone-maps HDR (PQ or HLG, BT.2020) down to 8-bit
 *  SDR BT.709 through zimg: linearise, convert primaries, compress the
 *  highlights with Hable's curve, then re-apply the BT.709 transfer. Without
 *  it a 10-bit HDR source squeezed into 8 bits keeps its HDR signal values
 *  and plays back grey and washed out. */
export function toneMapFilter(transfer: 'pq' | 'hlg'): string {
  const tin = transfer === 'pq' ? 'smpte2084' : 'arib-std-b67';
  return [
    `zscale=tin=${tin}:pin=bt2020:min=bt2020nc:t=linear:npl=100`,
    'format=gbrpf32le',
    'zscale=p=bt709',
    'tonemap=tonemap=hable:desat=0',
    'zscale=t=bt709:m=bt709:r=tv',
    'format=yuv420p',
  ].join(',');
}

/** `transpose` steps for a clockwise rotation. */
export function transposeFilters(rotation: 0 | 90 | 180 | 270): string[] {
  if (rotation === 90) return ['transpose=1'];
  if (rotation === 270) return ['transpose=2'];
  if (rotation === 180) return ['transpose=1', 'transpose=1'];
  return [];
}

function trimInputArgs(trim: ResolvedOptions['trim']): string[] {
  return trim ? ['-ss', trim.start.toFixed(3)] : [];
}

function trimOutputArgs(trim: ResolvedOptions['trim']): string[] {
  return trim ? ['-t', (trim.end - trim.start).toFixed(3)] : [];
}

/** One-line descriptions of the options a plan applies, appended to its reason. */
function optionNotes(o: ResolvedOptions, copied: boolean): string[] {
  const notes: string[] = [];
  if (o.trim) {
    const span = `${formatClock(o.trim.start)} to ${formatClock(o.trim.end)}`;
    notes.push(copied
      ? `Keeping ${span}. A fast cut starts at the nearest keyframe at or before ${formatClock(o.trim.start)}, so it can begin a second or two early - turn on "Exact cut" for a frame-accurate start.`
      : `Keeping ${span}, cut exactly.`);
  }
  if (o.rotate) {
    notes.push(copied
      ? `Rotated ${o.rotate}° clockwise by updating the file's rotation tag - instant, nothing re-encoded.`
      : `Rotated ${o.rotate}° clockwise.`);
  }
  if (o.removeAudio) notes.push('The audio track is left out.');
  return notes;
}

function planMp4(
  info: MediaInfo,
  inputName: string,
  webcodecs: WebCodecsSupport | null,
  o: ResolvedOptions,
): CorePlan {
  const outputName = 'output.mp4';

  const videoCodec = info.video ? normalizeCodec(info.video.codec) : null;
  const audioCodec = info.audio ? normalizeCodec(info.audio.codec) : null;
  const keepAudio = !!info.audio && !o.removeAudio;
  const videoOk = !videoCodec || MP4_VIDEO_CODECS.has(videoCodec);
  const audioOk = !keepAudio || !audioCodec || MP4_AUDIO_CODECS.has(audioCodec);
  const fps = info.video?.fps ?? 30;
  const srcRotation = normaliseRotation(info.video?.rotationDegrees ?? 0);
  const totalRotation = normaliseRotation(srcRotation + o.rotate);
  const keptSeconds = o.trim ? o.trim.end - o.trim.start : info.durationSeconds;
  const planNotes: string[] = [];

  // faststart plus use_metadata_tags: the moov atom is moved to the head so a
  // browser can start playing before the whole file is downloaded, and any
  // extra tag atoms in the source (rotation, creation time, encoder) are kept.
  const MP4_MUX_FLAGS = ['-movflags', '+faststart+use_metadata_tags'];

  // Maximum compatibility only makes sense when the browser can encode H.264;
  // the wasm fallback would turn a playable HEVC file into MPEG-4 Part 2,
  // which plays in fewer places, not more.
  let compatibility = o.compatibility && !!info.video;
  if (compatibility && !hasWebCodecsH264(webcodecs)) {
    planNotes.push('Maximum compatibility needs a browser with an H.264 encoder (Chrome, Edge or Safari 16.4+), so the video is kept as it is.');
    compatibility = false;
  }
  const compatNeedsEncode = compatibility
    && (videoCodec !== 'h264' || !!info.video?.highBitDepth || !!info.video?.hdrTransfer);

  // A file already under the target needs no compression at all.
  let targetBytes = info.video ? o.targetBytes : null;
  if (targetBytes && videoOk && !compatNeedsEncode && !o.maxShortEdge) {
    const projected = keptSeconds && info.durationSeconds
      ? info.sizeBytes * (keptSeconds / info.durationSeconds)
      : info.sizeBytes;
    if (projected <= targetBytes * 0.97) {
      planNotes.push(`It already fits in ${formatMb(targetBytes)}, so nothing needs compressing.`);
      targetBytes = null;
    }
  }

  const reencode = !!info.video
    && (!videoOk || compatNeedsEncode || !!targetBytes || !!o.maxShortEdge || !!o.trim?.exact);

  // ── Video copied into MP4 ────────────────────────────────────────────
  if (!reencode) {
    // Main audio track sized to its channel count when it has to become AAC.
    const ch = Math.min(6, Math.max(1, info.audio?.numChannels ?? 2));
    const aacKbps = Math.max(128, Math.min(384, ch * 96));
    const args = [
      '-hide_banner',
      ...INPUT_ROBUSTNESS,
      ...trimInputArgs(o.trim),
      ...rawFmtArgs(info),
      '-i', inputName,
      ...trimOutputArgs(o.trim),
      // `0:v?` / `0:a?` keep ALL video and audio tracks (not just the first).
      // Movies with multi-language audio, alt commentary, and secondary
      // video streams (angle switches on DVD) all survive the rewrap.
      // Subtitles are appended conditionally - only text-based subs (SubRip
      // / ASS / WebVTT) survive as mov_text in MP4; bitmap subs (PGS from
      // Blu-ray, VobSub from DVD) get dropped rather than fail the encode.
      '-map', '0:v?',
      ...(keepAudio ? ['-map', '0:a?'] : []),
      ...subtitleMapArgs(info.subtitles),
      '-map_metadata', '0',
      // Chapter marks would point at the wrong times after a cut.
      '-map_chapters', o.trim ? '-1' : '0',
    ];
    if (audioOk) {
      // `-c copy` is codec-copy for every stream; the subtitle override
      // below turns text subs into `mov_text`, the only text format MP4
      // carries. When there are no mapped subs it's a no-op.
      args.push('-c', 'copy');
      args.push(...subtitleCodecArgs(info.subtitles));
      args.push(...hevcTagArgs(videoCodec));
    } else {
      // Video already fits MP4 but the audio doesn't (AC-3, PCM, FLAC,
      // Opus, Vorbis, DTS): copy the video untouched, convert only the audio.
      // `-ac:a:0` targets only the main track: a stereo commentary track next
      // to a 5.1 mix stays stereo. Setting it (even to the source's own
      // count) also swaps AC-3's "5.1(side)" for the standard 5.1 layout;
      // without that the AAC encoder writes a non-standard channel map that
      // some players mis-route. Sources wider than 5.1 fold down to it.
      args.push(
        '-c:v', 'copy',
        ...hevcTagArgs(videoCodec),
        '-c:a', 'aac', '-ac:a:0', String(ch),
        // Main track sized to its channel count; extra tracks (commentary,
        // dubs - usually stereo) get the stereo rate.
        '-b:a', '192k', '-b:a:0', `${aacKbps}k`, '-aac_coder', 'twoloop',
        ...subtitleCodecArgs(info.subtitles),
      );
    }
    if (!keepAudio) args.push('-an');
    // On a stream copy, ffmpeg 5.1 turns this tag into the track's display
    // matrix, taking the angle counter-clockwise (QA: rotate=90 wrote the
    // matrix for a 90° anticlockwise turn). It replaces the source's own
    // matrix, so it carries the total turn, not just the extra one.
    if (o.rotate) args.push('-metadata:s:v:0', `rotate=${(360 - totalRotation) % 360}`);
    if (o.trim) args.push('-avoid_negative_ts', 'make_zero');
    args.push(...MUX_ROBUSTNESS, ...MP4_MUX_FLAGS, outputName);

    const extras: string[] = [];
    if (info.video?.hdr) extras.push('HDR colour metadata is preserved for HDR-capable displays.');
    if (info.video?.rotationDegrees && !o.rotate) extras.push(`${info.video.rotationDegrees}° rotation tag is preserved.`);
    const droppedBitmapSubs = info.subtitles.some((s) => !s.text);
    if (droppedBitmapSubs) extras.push('Bitmap subtitle tracks (PGS/VobSub) are dropped — MP4 can\'t carry them.');
    else if (info.subtitles.length) extras.push(`${info.subtitles.length} subtitle track${info.subtitles.length > 1 ? 's' : ''} pass${info.subtitles.length > 1 ? '' : 'es'} through as mov_text.`);
    extras.push(...optionNotes(o, true), ...planNotes);

    if (audioOk) {
      return {
        format: 'mp4',
        method: 'remux',
        encoder: 'ffmpeg',
        reason:
          `Your ${describeSource(info)} already sits inside MP4-compatible codecs — Wave rewraps the container without re-encoding a single frame. ` +
          `The result is byte-for-byte the same picture${keepAudio ? ' and sound' : ''}.` +
          (extras.length ? ` ${extras.join(' ')}` : ''),
        args,
        outputName,
        trim: o.trim ? { start: o.trim.start, end: o.trim.end } : null,
      };
    }
    return {
      format: 'mp4',
      method: 'transcode',
      encoder: 'ffmpeg',
      reason:
        `Video (${info.video?.codec ?? 'none'}) copies through untouched. ` +
        `Audio is converted from ${info.audio?.codec ?? 'the source'} to AAC at ${aacKbps} kbps so it plays in every browser and on every phone.` +
        (extras.length ? ` ${extras.join(' ')}` : ''),
      args,
      outputName,
      trim: o.trim ? { start: o.trim.start, end: o.trim.end } : null,
    };
  }

  // ── Video re-encoded ─────────────────────────────────────────────────
  // Output size in display orientation. Rotation (the source's own tag plus
  // any extra) is baked into the pixels, so 90° / 270° swap the dimensions.
  const srcW = info.video?.displayWidth ?? info.video?.width ?? 1280;
  const srcH = info.video?.height ?? 720;
  const swapped = totalRotation === 90 || totalRotation === 270;
  let outW = swapped ? srcH : srcW;
  let outH = swapped ? srcW : srcH;
  let capEdge = o.maxShortEdge;

  let bitrateMode: 'variable' | 'constant' = 'variable';
  let targetVideoBps: number | null = null;
  let audioKbps: number | null = null;
  let audioChannels: number | null = null;
  let blocker: string | undefined;
  if (targetBytes) {
    if (!keptSeconds) {
      blocker = 'Wave can\'t read this video\'s length, so it can\'t aim for a file size.';
    } else {
      const budget = targetBudget(targetBytes, keptSeconds, fps, keepAudio);
      audioKbps = budget.audioKbps;
      if (keepAudio) audioChannels = 2;
      if (budget.blocked) {
        blocker = `${formatMb(targetBytes)} is too small for ${formatClock(keptSeconds)} of video - it needs at least about ${formatMb(budget.minBytes)} to stay watchable. Trim it shorter or choose a bigger size.`;
      }
      targetVideoBps = budget.videoBps;
      if (budget.shortEdge && (!capEdge || budget.shortEdge < capEdge)) capEdge = budget.shortEdge;
      bitrateMode = 'constant';
    }
  }
  if (capEdge) {
    const short = Math.min(outW, outH);
    if (short > capEdge) {
      const f = capEdge / short;
      outW = outW * f;
      outH = outH * f;
    }
  }
  outW = even(outW);
  outH = even(outH);
  const defaultBps = defaultVideoBitrate(outW, outH, fps);
  let bitrate = defaultBps;
  if (targetVideoBps != null) {
    // Never spend more than the normal-quality rate: if the target is
    // generous, the file simply comes out smaller than it.
    if (targetVideoBps < defaultBps) bitrate = targetVideoBps;
    else bitrateMode = 'variable';
  }
  const scaled = outW !== even(swapped ? srcH : srcW) || outH !== even(swapped ? srcW : srcH);
  const toneMap = info.video?.hdrTransfer ?? null;

  const effectNotes: string[] = [];
  if (toneMap) {
    effectNotes.push(`The source is HDR (${toneMap === 'pq' ? 'PQ' : 'HLG'}); Wave converts it to standard colours so it doesn't look washed out on ordinary screens.`);
  } else if (info.video?.highBitDepth && !compatNeedsEncode) {
    effectNotes.push('The 10-bit source is converted to standard 8-bit video, which every player supports.');
  }
  if (targetBytes && targetVideoBps != null && !blocker) {
    effectNotes.push(
      `Aiming for ${formatMb(targetBytes)}: video at ${Math.round(bitrate / 1000)} kbps` +
      (scaled ? `, scaled to ${outW}×${outH} so the picture holds up at that rate` : '') +
      (keepAudio ? `, audio at ${audioKbps} kbps stereo.` : '.'),
    );
  } else if (scaled) {
    effectNotes.push(`Scaled to ${outW}×${outH}.`);
  }
  effectNotes.push(...optionNotes(o, false), ...planNotes);

  const wcStrategy = pickWebCodecsStrategy(info, webcodecs, !!o.trim || !!o.rotate || !!toneMap);
  if (wcStrategy) {
    const profile = pickProfileLabel(webcodecs!);
    const hw = webcodecs!.hardwareAccelerated ? ', hardware-accelerated,' : '';
    const srcName = prettyCodec(info.video?.codec);
    const reasonBits: string[] = [];
    // HDR / 10-bit → HEVC Main 10 when the browser can encode it AND we're
    // on the browser-decode path (Path B normalises to 8-bit yuv420p, so
    // 10-bit info would be lost before the encoder saw it). Compatibility
    // mode always wants H.264, so it never takes this branch.
    const wantsHevc =
      wcStrategy === 'browser'
      && !compatibility
      && !!(info.video?.hdr || info.video?.highBitDepth)
      && !!webcodecs!.hevcMain10;
    const videoCodecOut: 'h264' | 'hevc' = wantsHevc ? 'hevc' : 'h264';
    if (wantsHevc) {
      reasonBits.push(
        `Source is HDR / 10-bit — Wave re-encodes to HEVC Main 10${hw} through your browser's WebCodecs encoder so the HDR colour depth is preserved, and wraps it in an MP4 for player compatibility.`
      );
    } else if (compatNeedsEncode && videoOk) {
      const why = videoCodec === 'hevc'
        ? 'including Windows PCs without the HEVC extension'
        : videoCodec === 'av1'
          ? 'including older phones, TVs and PCs with no AV1 decoder'
          : 'unlike 10-bit H.264, which most hardware decoders refuse';
      reasonBits.push(
        `For maximum compatibility, Wave re-encodes the ${srcName} video to H.264 ${profile}${hw} with your browser's built-in encoder. H.264 plays on every phone, PC, TV and browser, ${why}.`
      );
    } else {
      reasonBits.push(
        wcStrategy === 'webcodecs'
          ? `Wave decodes the ${srcName} video with your browser's own video decoder and re-encodes it to H.264 ${profile}${hw} with its built-in encoder, so the result is a standard H.264 MP4.`
          : `Wave decodes the ${srcName} video on your device and re-encodes it to H.264 ${profile}${hw} with your browser's built-in encoder, so the result is a standard H.264 MP4.`
      );
    }
    // A size target already names the audio rate in its own note.
    if (keepAudio && info.audio && !targetBytes) {
      reasonBits.push(audioCodec === 'aac'
        ? 'The audio is re-encoded to AAC as well.'
        : `The ${prettyCodec(info.audio.codec)} audio is converted to AAC.`);
    }
    if (info.video?.variableFramerate && wcStrategy === 'browser') {
      reasonBits.push('Source has variable frame timing; Wave preserves it in the re-encode.');
    }
    if (info.video?.interlaced) {
      reasonBits.push(
        wcStrategy === 'ffmpeg'
          ? 'Source is interlaced; Wave de-interlaces with yadif so the output plays cleanly on progressive displays.'
          : 'Source is interlaced; the browser\'s decoder de-interlaces frames before they reach the encoder.'
      );
    }
    if (info.video?.sampleAspectRatio && info.video.sampleAspectRatio !== 1) {
      reasonBits.push(
        `Anamorphic pixel shape (SAR ${info.video.sampleAspectRatio.toFixed(3)}) is corrected — encode is at display dimensions so square-pixel players show the picture in the intended aspect ratio.`
      );
    }
    reasonBits.push(...effectNotes);
    return {
      format: 'mp4',
      method: 'transcode',
      encoder: 'webcodecs',
      decodeStrategy: wcStrategy,
      videoCodec: videoCodecOut,
      reason: reasonBits.join(' '),
      args: [],
      outputName,
      encode: {
        width: outW,
        height: outH,
        bitrate,
        bitrateMode,
        toneMap: wantsHevc ? null : toneMap,
        rotation: totalRotation,
      },
      audio: { remove: !keepAudio, bitrateKbps: audioKbps, channels: audioChannels },
      trim: o.trim ? { start: o.trim.start, end: o.trim.end } : null,
      blocker,
      targetBytes,
    };
  }

  // ── Fallback: wasm MPEG-4 Part 2 encoder (no WebCodecs H.264) ────────
  const qscale = info.video ? pickQscale(info.video.width, info.video.height) : 4;
  const args: string[] = [
    '-hide_banner',
    ...INPUT_ROBUSTNESS,
    ...trimInputArgs(o.trim),
    ...rawFmtArgs(info),
    '-i', inputName,
    ...trimOutputArgs(o.trim),
    '-map', '0:v:0?',
    ...(keepAudio ? ['-map', '0:a:0?'] : []),
    '-map_metadata', '0',
  ];

  // Variable-framerate sources (screen recordings, some phone captures) turn
  // into stuttering messes when the encoder is forced to a fixed rate. `vfr`
  // tells the encoder to keep the source's actual frame timing.
  if (info.video?.variableFramerate) args.push('-vsync', 'vfr');

  args.push('-c:v', FALLBACK_VIDEO_CODEC);
  if (targetBytes && targetVideoBps != null) {
    args.push('-b:v', String(bitrate));
  } else {
    args.push('-qscale:v', String(qscale));
  }
  args.push(...MPEG4_TUNING);
  // Force 8-bit 4:2:0 for broad player compatibility.
  args.push('-pix_fmt', 'yuv420p');
  // ffmpeg applies the source's rotation tag itself when decoding, so only
  // the extra rotation needs a transpose here. yadif de-interlaces old TV /
  // DVD captures; scale corrects anamorphic pixels and applies any size cap;
  // the tone-map chain turns HDR into SDR before the 8-bit encode.
  const vf: string[] = [];
  if (info.video?.interlaced) vf.push('yadif=1');
  vf.push(...transposeFilters(o.rotate));
  const anamorphic = !!(info.video?.sampleAspectRatio && info.video.sampleAspectRatio !== 1);
  if (scaled || anamorphic) vf.push(`scale=${outW}:${outH}:flags=fast_bilinear,setsar=1`);
  if (toneMap) vf.push(toneMapFilter(toneMap));
  if (vf.length) args.push('-vf', vf.join(','));
  if (!keepAudio) {
    args.push('-an');
  } else {
    args.push('-c:a', audioOk && !audioKbps ? 'copy' : FALLBACK_AUDIO_CODEC);
    if (!audioOk || audioKbps) {
      args.push('-b:a', `${audioKbps ?? 192}k`);
      if (audioChannels) args.push('-ac', String(audioChannels));
      // Two-loop is the higher-quality AAC coder in ffmpeg's native encoder.
      args.push('-aac_coder', 'twoloop');
    }
  }
  args.push(...MUX_ROBUSTNESS, ...MP4_MUX_FLAGS, outputName);

  const reasonBits: string[] = [];
  if (info.video) {
    const res = info.video.width && info.video.height ? `${info.video.width}×${info.video.height}` : 'the source';
    const fpsPart = info.video.fps ? ` @ ${info.video.fps.toFixed(0)} fps` : '';
    reasonBits.push(
      targetBytes
        ? `Re-encoding video from ${info.video.codec} to MPEG-4 at ${Math.round(bitrate / 1000)} kbps.`
        : `Re-encoding video from ${info.video.codec} to MPEG-4 at ${qualityLabel(qscale)} (qscale ${qscale}, chosen for ${res}${fpsPart}).`
    );
    // This branch fires when the WebCodecs H.264 encoder isn't available in
    // the browser (older Firefox on Linux, Firefox with hardware-video-decoding
    // disabled, some sandboxed builds). The LGPL-safe wasm fallback is mpeg4,
    // which is spec-legal inside MP4 but no consumer browser decodes it — the
    // output plays only in VLC / QuickTime. Warn up-front so the user isn't
    // surprised when their "converted" MP4 refuses to play in the same browser
    // they made it in. See project-transcode-backend memory.
    reasonBits.push(
      'Heads up: your browser doesn\'t expose an H.264 encoder, so the output uses the LGPL MPEG-4 fallback. The MP4 plays in VLC, QuickTime, and most desktop players, but not in Chrome / Safari / mobile browsers. For a browser-playable output use a Chromium-based browser (Chrome, Edge, Brave, Opera) or Safari 16.4+.'
    );
    if (info.video.variableFramerate) {
      reasonBits.push('Source has variable frame timing; Wave preserves it in the re-encode.');
    }
  }
  if (keepAudio && !audioOk && info.audio) {
    reasonBits.push(`Re-encoding audio from ${info.audio.codec} to AAC at ${audioKbps ?? 192} kbps.`);
  }
  if (keepAudio && audioOk && info.audio && !audioKbps) reasonBits.push(`Audio (${info.audio.codec}) copies through untouched.`);
  if (info.video?.interlaced) {
    reasonBits.push('Source is interlaced; Wave de-interlaces with yadif so the output plays cleanly on progressive displays.');
  }
  if (anamorphic && info.video?.sampleAspectRatio) {
    reasonBits.push(
      `Anamorphic pixel shape (SAR ${info.video.sampleAspectRatio.toFixed(3)}) is corrected — encode is at display dimensions so square-pixel players show the picture in the intended aspect ratio.`
    );
  }
  reasonBits.push(...effectNotes);

  return {
    format: 'mp4',
    method: 'transcode',
    encoder: 'ffmpeg',
    reason: reasonBits.join(' '),
    args,
    outputName,
    trim: o.trim ? { start: o.trim.start, end: o.trim.end } : null,
    blocker,
    targetBytes,
  };
}

function pickProfileLabel(s: WebCodecsSupport): string {
  if (s.h264High) return 'High profile';
  if (s.h264Main) return 'Main profile';
  return 'Baseline profile';
}

/** Codec names as people know them: "hevc" reads as HEVC (H.265). */
function prettyCodec(raw: string | undefined): string {
  if (!raw) return 'source';
  const c = normalizeCodec(raw);
  const names: Record<string, string> = {
    h264: 'H.264', hevc: 'HEVC (H.265)', av1: 'AV1', vp8: 'VP8', vp9: 'VP9', mpeg4: 'MPEG-4 Part 2',
    mpeg2video: 'MPEG-2', mpeg1video: 'MPEG-1', wmv3: 'WMV', wmv2: 'WMV', vc1: 'VC-1', prores: 'ProRes',
    mjpeg: 'Motion JPEG', aac: 'AAC', mp3: 'MP3', ac3: 'Dolby Digital (AC-3)', eac3: 'Dolby Digital Plus',
    dts: 'DTS', opus: 'Opus', vorbis: 'Vorbis', flac: 'FLAC', wmav2: 'WMA', alac: 'ALAC',
  };
  if (names[c]) return names[c];
  if (c.startsWith('pcm_')) return 'PCM';
  return raw.toUpperCase();
}

function planMp3(info: MediaInfo, inputName: string, o: ResolvedOptions): CorePlan {
  const outputName = 'output.mp3';
  // Guard: MP3 extraction requires an audio track. Without one, ffmpeg
  // still tries to write an MP3 with no input stream and the wasm build
  // aborts with an opaque "Aborted()" — worse than a friendly error. The
  // inspect UI hides the MP3 button in this case, but callers that skip
  // the UI (direct engine use, deep-linked convert on an audio-less
  // source) reach this path. Fail up front with something the caller can
  // surface.
  if (!info.audio) {
    throw new Error('This source has no audio track — MP3 extraction is not possible.');
  }
  const audioCodec = info.audio ? normalizeCodec(info.audio.codec) : null;
  const trim = o.trim ? { start: o.trim.start, end: o.trim.end } : null;
  // MP3 frames are 26 ms long, so a copied MP3 cut is accurate to the frame.
  const trimNote = o.trim ? ` Keeping ${formatClock(o.trim.start)} to ${formatClock(o.trim.end)}.` : '';

  if (audioCodec === 'mp3') {
    return {
      format: 'mp3',
      method: 'remux',
      encoder: 'ffmpeg',
      reason:
        `The source's audio track is already MP3 — Wave extracts it into a standalone .mp3 without re-encoding, so the audio is bit-exact.` + trimNote,
      args: [
        '-hide_banner',
        ...INPUT_ROBUSTNESS,
        ...trimInputArgs(o.trim),
        ...rawFmtArgs(info),
        '-i', inputName,
        ...trimOutputArgs(o.trim),
        '-map', '0:a:0?',
        '-vn',
        '-c:a', 'copy',
        ...MUX_ROBUSTNESS,
        outputName,
      ],
      outputName,
      trim,
    };
  }

  // libmp3lame is the LGPL-safe MP3 encoder in @ffmpeg/core.
  // -q:a 2 = VBR ~190 kbps average, transparent for most sources.
  const args = [
    '-hide_banner',
    ...INPUT_ROBUSTNESS,
    ...trimInputArgs(o.trim),
    ...rawFmtArgs(info),
    '-i', inputName,
    ...trimOutputArgs(o.trim),
    '-map', '0:a:0?',
    '-vn',
    '-c:a', 'libmp3lame',
    '-q:a', '2',
    ...MUX_ROBUSTNESS,
    outputName,
  ];

  const codecName = info.audio?.codec ?? 'the audio track';
  return {
    format: 'mp3',
    method: 'transcode',
    encoder: 'ffmpeg',
    reason:
      `Extracting the ${codecName} audio track and encoding it as MP3 at high-quality VBR (~190 kbps average). ` +
      `Wave uses LAME's variable-bitrate mode so tonally dense sections get more bits than quiet ones — smaller than 320 kbps CBR for the same fidelity.` + trimNote,
    args,
    outputName,
    trim,
  };
}

export function outputExtension(fmt: OutputFormat): string {
  return fmt;
}

export function outputMimeType(fmt: OutputFormat): string {
  return fmt === 'mp3' ? 'audio/mpeg' : 'video/mp4';
}

export function isAudioFormat(fmt: OutputFormat): boolean {
  return fmt === 'mp3';
}
